/**
 * useBrainConfig Hook
 *
 * React Query hook for fetching brain-owned configurations.
 * Calls the unified /api/config/brain/:type endpoint.
 */

import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '../providers/SdkProvider';
import { BrainConfigService, type BrainConfigType } from '../services/brain-config.service';

// ═══════════════════════════════════════════════════════════════════════
// QUERY KEYS
// ═══════════════════════════════════════════════════════════════════════

export const brainConfigKeys = {
  all: ['brainConfig'] as const,
  config: (type: string, market?: string) =>
    [...brainConfigKeys.all, type, market ?? 'global'] as const,
};

// ═══════════════════════════════════════════════════════════════════════
// HOOKS
// ═══════════════════════════════════════════════════════════════════════

/**
 * Fetch a brain-owned configuration by type.
 *
 * @param configType  - e.g. 'iqulture', 'matching_weights', 'checkup_assessment'
 * @param options     - Optional market and enabled flag
 */
export function useBrainConfig(
  configType: BrainConfigType,
  options?: { market?: string; enabled?: boolean }
) {
  const client = useApiClient();
  const service = new BrainConfigService(client);

  return useQuery({
    queryKey: brainConfigKeys.config(configType, options?.market),
    queryFn: () => service.getConfig(configType, options?.market),
    enabled: options?.enabled !== false,
    staleTime: 30 * 60 * 1000, // 30 minutes — configs change rarely
    gcTime: 60 * 60 * 1000, // 1 hour cache
    retry: 2,
  });
}
